import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

export type CheckboxSize = 'sm' | 'md' | 'lg';

/**
 * Checkbox atom: circular design, brand primary when checked, works with FormControl.
 * Accessible: native input (sr-only), visual circle + checkmark via peer modifiers.
 */
@Component({
  selector: 'om-checkbox',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './checkbox.html',
  styleUrl: './checkbox.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Checkbox {
  control = input.required<FormControl<boolean | null>>();
  inputId = input<string>('');
  size = input<CheckboxSize>('md');

  boxClasses = computed(() => {
    const sizeMap: Record<CheckboxSize, string> = {
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };
    const sizeClass = sizeMap[this.size()];
    return [
      'inline-flex shrink-0 items-center justify-center rounded-full border-2 border-white/50 dark:border-white/[0.20] bg-white/70 dark:bg-white/[0.08] backdrop-blur-sm transition-colors duration-200',
      'peer-checked:border-primary peer-checked:bg-primary',
      'peer-checked:[&_.checkmark]:opacity-100',
      'peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary peer-focus:ring-offset-2',
      'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
      sizeClass,
    ].join(' ');
  });

  checkmarkClasses = computed(() => {
    const sizeMap: Record<CheckboxSize, string> = {
      sm: 'w-2 h-2',
      md: 'w-2.5 h-2.5',
      lg: 'w-3 h-3',
    };
    return `checkmark opacity-0 transition-opacity ${sizeMap[this.size()]}`;
  });
}
